import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAnimation } from '../../hooks/useAnimation';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './Features.scss';
import { useNavigate } from 'react-router-dom';
import fabricService from '../../services/fabricService';
import config from '../../config';

const Features = () => {
  const { t } = useTranslation();
  const { slideInLeft, bounceIn, slideInRight, fadeOnly, staggerContainer } = useAnimation();
  const navigate = useNavigate();
  const [fabrics, setFabrics] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Slider state
  const [current, setCurrent] = useState(0);
  const visibleCount = 3;
  const total = fabrics.length;

  useEffect(() => {
    loadFabrics();
  }, []);

  const loadFabrics = async () => {
    try {
      setLoading(true);
      const data = await fabricService.getAllFabrics();
      setFabrics(data);
    } catch (error) {
      console.error('Failed to load fabrics:', error);
      // Fallback to hardcoded data if API fails
      setFabrics([
        {
          id: 1,
          name: t('features.cotton.title'),
          description: t('features.cotton.description'),
          main_image: '/uploads/fabrics/fabric-type-cotton.png'
        },
        {
          id: 2,
          name: t('features.linen.title'),
          description: t('features.linen.description'),
          main_image: '/uploads/fabrics/fabric-type-linen.png'
        },
        {
          id: 3,
          name: t('features.twill.title'),
          description: t('features.twill.description'),
          main_image: '/uploads/fabrics/fabric-type-twill.png'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => {
    setCurrent((prev) => (prev - 1 + total) % total);
  };
  const handleNext = () => {
    setCurrent((prev) => (prev + 1) % total);
  };

  const handleCardClick = (fabricId) => {
    navigate('/fabrics');
  };

  // Slider logic: always show 3, wrap around
  const getVisibleFabrics = () => {
    if (total <= visibleCount) return fabrics;
    const arr = [];
    for (let i = 0; i < visibleCount; i++) {
      arr.push(fabrics[(current + i) % total]);
    }
    return arr;
  };

  const visibleFabrics = getVisibleFabrics();
  const cardVariants = [slideInLeft, bounceIn, slideInRight];

  if (loading) {
    return (
      <section id="about" className="features-section">
        <Container>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <section id="about" className="features-section">
      <Container>
        <motion.div 
          className="section-header text-center mb-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.3 }}
          variants={fadeOnly}
        >
          <h2>{t('features.title')}</h2>
          <p className="section-description">
            {t('features.description')}
          </p>
          <motion.button
            className="btn btn-primary btn-lg mt-3"
            onClick={() => navigate('/fabrics')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('features.view_all')}
          </motion.button>
        </motion.div>
        <div className="d-flex justify-content-center align-items-center mb-3">
          <button className="slider-arrow" onClick={handlePrev} aria-label="Önceki">
            <ChevronLeft size={32} />
          </button>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            className="w-100"
          >
            <Row>
              {visibleFabrics.map((fabric, index) => (
                <Col md={4} key={fabric.id}>
                  <motion.div
                    className="feature-card"
                    variants={cardVariants[index]}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleCardClick(fabric.id)}
                  >
                    <div className="feature-image">
                      <img 
                        src={fabric.main_image ? (() => {
                          // Eğer path zaten /api ile başlıyorsa, sadece domain ekle
                          if (fabric.main_image.startsWith('/api/')) {
                            return `https://tekstas.com${fabric.main_image}`;
                          }
                          // Eğer path /uploads ile başlıyorsa, API_URL ile birleştir
                          else if (fabric.main_image.startsWith('/uploads/')) {
                            return `${config.API_URL}${fabric.main_image}`;
                          }
                          // Eğer path assets/images ile başlıyorsa, require kullan
                          else if (fabric.main_image.includes('assets/images/')) {
                            return require(`../../assets/images/${fabric.main_image.split('/').pop()}`);
                          }
                          // Diğer durumlar için API_URL ile birleştir
                          else {
                            return `${config.API_URL}${fabric.main_image}`;
                          }
                        })() : 
                          require('../../assets/images/fabric-type-cotton.png')
                        } 
                        alt={fabric.name}
                        onError={(e) => {
                          e.target.src = require('../../assets/images/fabric-type-cotton.png');
                        }}
                      />
                      <div className="feature-overlay">
                        <h3>{fabric.name}</h3>
                        <p>{fabric.description}</p>
                      </div>
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
          <button className="slider-arrow" onClick={handleNext} aria-label="Sonraki">
            <ChevronRight size={32} />
          </button>
        </div>
      </Container>
    </section>
  );
};

export default Features;